import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { Tab, Tabs } from '@mui/material/';

type HeaderLink = {
  level: number;
  text: string;
  content: string;
};

const StickyBox = styled.div<{ noHeaders?: boolean }>`
  display: flex;
  position: sticky;
  top: 0;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.background.standard};

  & .MuiTabs-indicator {
    left: 0;
    background: ${({ theme }) => theme.text.standard};
  }

  & .MuiTab-root.Mui-selected {
    color: ${({ theme }) => theme.text.standard};
  }

  & .MuiTab-root {
    display: flex;
    align-items: flex-start;
    white-space: nowrap;
    font-weight: 400;
    text-transform: none;
    padding: 2px 12px;
    font-family: 'Inter';
    min-width: 300px;
    font-size: 11.5px !important;
    min-height: 32px;
    padding-left: 24px;
  }

  ${({ noHeaders }) => noHeaders && `display: none`};

  @media (max-width: 1620px) {
    display: none;
  }
`;

const IndentedTab = styled(Tab)<{ level: number }>`
  padding-left: ${({ level }) => level * 12}px !important;
`;

interface Props {
  mkDocument: string;
}

export const DocsHeaders: React.FC<Props> = ({ mkDocument }) => {
  const [value, setValue] = useState(0);

  // Reset the selected tab when the document changes
  useEffect(() => {
    setValue(0);
  }, [mkDocument]);

  const extractHeaders = (mkDocument: string): HeaderLink[] => {
    const headers: HeaderLink[] = [];
    const headerRegex = /^(#+)\s(.*)$/gm;
    let match;

    while ((match = headerRegex.exec(mkDocument)) !== null) {
      const level = match[1].length;
      const text = match[2];
      headers.push({ level, text, content: '' });
    }

    return headers;
  };

  const extractContent = (mkDocument: string, headers: HeaderLink[]): HeaderLink[] => {
    headers.forEach((header, index) => {
      const startIndex = mkDocument.indexOf(header.text) + header.text.length;
      const endIndex = index + 1 < headers.length ? mkDocument.indexOf(headers[index + 1].text) : mkDocument.length;
      header.content = mkDocument.slice(startIndex, endIndex).trim();
    });
    return headers;
  };

  const convertToHref = (text: string) => {
    return `#${text
      .toLowerCase()
      .replace(/ /g, '-')
      .replace(/[^\w-]+/g, '')}`;
  };

  const headers = extractHeaders(mkDocument);
  const populatedHeaders = extractContent(mkDocument, headers);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    window.location.hash = convertToHref(populatedHeaders[newValue].text);
  };

  return (
    <StickyBox noHeaders={!populatedHeaders.length}>
      <Tabs
        orientation='vertical'
        variant='scrollable'
        value={value}
        onChange={handleChange}
        aria-label='Vertical tabs example'
        sx={{ borderLeft: 1, borderColor: 'divider' }}
      >
        {populatedHeaders.map((header, index) => (
          <IndentedTab
            key={index}
            title={header.text}
            label={header.text.length > 36 ? `${header.text.substring(0, 36)}...` : header.text}
            level={header.level || 1}
          />
        ))}
      </Tabs>
    </StickyBox>
  );
};
